import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ApplicationDetail, CodeValidationConflictCandidatesResponse, DashboardResponse, RecentCodeAssessment, Strategies, TopApplicationsResponse } from './dashboard.models';

@Injectable({
    providedIn: 'root'
})
export class DashboardService {
    constructor(private http: HttpClient) { }

    getDashboard(): Observable<DashboardResponse> {
        return this.http.get<DashboardResponse>(`${environment.apiUrl}/dashboard/portfolio-details?page=1&pageSize=5`);
    }

    getApplicationsPortfolio(pageSize: number, page: number, filters?: any[], assignedByOM?: boolean, assessmentstatus: string = '', modernizationstatus: string = '', strategystatus: string = '', type: string = '', priority: string = '', showRejectedApps?: boolean, showMyWork?: boolean): Observable<DashboardResponse> {
        let url = `${environment.apiUrl}/dashboard/portfolio-details?page=${page}&pageSize=${pageSize}`;

        if (assignedByOM !== undefined && assignedByOM !== null) {
            url += `&assignedByOM=${assignedByOM}`;
        }

        if (assessmentstatus) {
            url += `&assessmentstatus=${encodeURIComponent(assessmentstatus)}`;
        }

        if (modernizationstatus) {
            url += `&modernizationstatus=${encodeURIComponent(modernizationstatus)}`;
        }

        if (strategystatus) {
            url += `&strategystatus=${encodeURIComponent(strategystatus)}`;
        }

        if (type) {
            url += `&type=${encodeURIComponent(type)}`;
        }

        if (priority) {
            url += `&priority=${encodeURIComponent(priority)}`;
        }

        if (showRejectedApps !== undefined && showRejectedApps !== null) {
            url += `&showRejectedApps=${showRejectedApps}`;
        }

        if (showMyWork) {
            url += `&showMyWork=true`;
        }

        // Send multi-value filters as repeated query parameters so ASP.NET binds string[] correctly.
        if (filters && filters.length > 0) {
            filters.forEach(filter => {
                const values = filter.value.split('\u001F').map((v: string) => v.trim()).filter((v: string) => v);
                if (values.length > 0) {
                    values.forEach((value: string) => {
                        url += `&${filter.column}=${encodeURIComponent(value)}`;
                    });
                }
            });
        }
        return this.http.get<DashboardResponse>(url);
    }

    getStrategies(): Observable<Strategies> {
        return this.http.get<Strategies>(`${environment.apiUrl}/dashboard/strategies`);
    }

    getApplicationByTrackedBusinessApplicationId(trackedBusinessApplicationId: number): Observable<ApplicationDetail | null> {
        const url = `${environment.apiUrl}/dashboard/application/${trackedBusinessApplicationId}`;
        return this.http.get<ApplicationDetail>(url).pipe(
            map(response => response),
            catchError(() => of(null))
        );
    }

    getFilterOptions(assessmentstatus: string = '', strategystatus: string = '', type: string = '', priority: string = ''): Observable<{ [key: string]: string[] }> {
        const params: string[] = [];
        if (assessmentstatus) { params.push(`assessmentstatus=${encodeURIComponent(assessmentstatus)}`); }
        if (strategystatus) { params.push(`strategystatus=${encodeURIComponent(strategystatus)}`); }
        if (type) { params.push(`type=${encodeURIComponent(type)}`); }
        if (priority) { params.push(`priority=${encodeURIComponent(priority)}`); }
        const url = `${environment.apiUrl}/dashboard/filter-options${params.length ? '?' + params.join('&') : ''}`;
        return this.http.get<{ [key: string]: string[] }>(url);
    }

    getTopApplications(): Observable<TopApplicationsResponse> {
        return this.http.get<TopApplicationsResponse>(`${environment.apiUrl}/dashboard/top-applications`);
    }

    getRecentCodeAssessments(): Observable<RecentCodeAssessment[]> {
        return this.http.get<RecentCodeAssessment[]>(`${environment.apiUrl}/dashboard/recent-code-assessments`);
    }

    getCodeValidationConflictCandidates(topCount: number = 10): Observable<CodeValidationConflictCandidatesResponse> {
        return this.http.get<CodeValidationConflictCandidatesResponse>(`${environment.apiUrl}/dashboard/code-validation-conflicts?topCount=${topCount}`);
    }
}